// Typed API client for chat endpoints
import {
  mockCreateNewChat,
  mockSendMessage,
  mockFetchChatThreads,
  mockFetchThreadMessages,
} from "./mockApi";

export interface JgsStudentData {
  player_name?: string;
  rank?: number;
  class_rank?: number;
  class_year?: string | number;
  scoring_differential?: string | number;
  gender?: string;
  hometown?: string;
  state?: string;
  student_id?: string;
  events?: number | null;
  strength_of_tournament?: number | null;
  [key: string]: unknown; // Allow additional fields from API
}

export interface AjgaTournament {
  event?: string;
  start_date?: string;
  place?: number | null;
  participant_count?: number;
  scores?: string | null;
  total?: number | null;
  points?: number | null;
}

export interface AjgaData {
  rank?: number;
  events_played?: number;
  avg_points?: number;
  total_points?: number;
  bonus_points?: number;
  country?: string;
  tournaments?: AjgaTournament[];
}

export interface NewChatResponse {
  threadId: string;
  user?: { id?: string; email?: string; hasJgsData?: boolean };
  jgs_student_data?: JgsStudentData | null;
}

export interface CoachLinkedProfile {
  jgsProfileId: string;
  studentId?: string;
  displayName?: string;
  linkedAt?: string;
  jgsData?: JgsStudentData;
  ajgaData?: AjgaData;
}

export interface MeResponse {
  success: boolean;
  user_id: string;
  email?: string;
  userType?: "player" | "coach";
  hasPassword?: boolean;
  has_jgs_mapping?: boolean;
  jgs_student_data?: JgsStudentData | null;
  ajga_data?: AjgaData | null;
  linkedProfiles?: CoachLinkedProfile[];
  isReturningUser?: boolean;
}

export interface SendMessageRequest {
  threadId: string;
  prompt: string;
  stream?: boolean;
  signal?: AbortSignal;
}

export interface ThreadListItem {
  threadId: string;
  title?: string;
  lastUpdated: number;
  messageCount?: number;
}

export interface ThreadMessagesResponse {
  threadId: string;
  messages: Array<{ id: string; role: "user" | "assistant"; text: string; createdAt: string }>;
}

const API_BASE_URL: string = import.meta.env.VITE_API_URL || "";
const DEV_MODE: boolean = import.meta.env.VITE_DEV_MODE === "true";

function withBase(path: string): string {
  return `${API_BASE_URL.replace(/\/$/, "")}${path}`;
}

export async function createNewChat(linkedJgsProfile?: string): Promise<NewChatResponse> {
  // Use mock in dev mode
  if (DEV_MODE) {
    return mockCreateNewChat();
  }

  const body: Record<string, unknown> = {};
  if (linkedJgsProfile) body.linkedJgsProfile = linkedJgsProfile;

  const resp = await fetch(withBase("/chats/newChat"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify(body),
  });
  if (!resp.ok) {
    const t = await resp.text().catch(() => "");
    throw new Error(t || `Failed to create chat (${resp.status})`);
  }
  return resp.json();
}

export async function sendMessage(req: SendMessageRequest): Promise<Response> {
  // Use mock in dev mode
  if (DEV_MODE) {
    return mockSendMessage(req);
  }

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (req.stream) {
    headers["Accept"] = "text/event-stream";
  } else {
    headers["Accept"] = "application/json";
  }

  const resp = await fetch(withBase("/chats/message"), {
    method: "POST",
    headers,
    credentials: "include",
    body: JSON.stringify({
      threadId: req.threadId,
      prompt: req.prompt,
      stream: req.stream ?? true,
    }),
    signal: req.signal,
  });
  if (!resp.ok) {
    const t = await resp.text().catch(() => "");
    throw new Error(t || `Failed to send message (${resp.status})`);
  }
  return resp;
}

// Export dev mode flag for debugging
export const isDevMode = DEV_MODE;

/**
 * Fetch chat history for the current authenticated user
 */
export async function fetchChatThreads(
  page: number = 1,
  pageSize: number = 20,
  linkedJgsProfile?: string
): Promise<ThreadListItem[]> {
  if (DEV_MODE) {
    return mockFetchChatThreads();
  }

  const url = new URL(withBase("/chats/threads"));
  url.searchParams.set("page", String(page));
  url.searchParams.set("pageSize", String(pageSize));
  if (linkedJgsProfile) url.searchParams.set("linkedJgsProfile", linkedJgsProfile);
  const resp = await fetch(url.toString(), {
    method: "GET",
    credentials: "include",
  });
  if (!resp.ok) {
    return [];
  }
  const data = await resp.json().catch(() => []);
  // Support both array and paginated object shapes
  if (Array.isArray(data)) {
    // Assume already in ThreadListItem-like shape
    return data.map((t: any) => ({
      threadId: t.threadId,
      title: t.title,
      lastUpdated: typeof t.lastUpdated === "number" ? t.lastUpdated : Date.now(),
      messageCount: typeof t.messageCount === "number" ? t.messageCount : undefined,
    }));
  }
  if (data && Array.isArray(data.items)) {
    return data.items.map((item: any) => ({
      threadId: String(item.threadId),
      title: item.title || "New Chat",
      lastUpdated: item.lastMessageAt ? Date.parse(item.lastMessageAt) : Date.now(),
      messageCount: typeof item.messageCount === "number" ? item.messageCount : undefined,
    }));
  }
  return [];
}

export class RateLimitError extends Error {
  retryAfterSeconds?: number;
  constructor(message: string, retryAfterSeconds?: number) {
    super(message);
    this.name = "RateLimitError";
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

export async function fetchThreadMessages(
  threadId: string,
  limit: number = 50
): Promise<ThreadMessagesResponse> {
  if (DEV_MODE) {
    return await mockFetchThreadMessages(threadId, limit);
  }

  const url = new URL(withBase(`/chats/threads/${encodeURIComponent(threadId)}/messages`));
  if (limit) url.searchParams.set("limit", String(limit));

  const resp = await fetch(url.toString(), {
    method: "GET",
    credentials: "include",
  });

  if (!resp.ok) {
    if (resp.status === 429) {
      const retryHeader = resp.headers.get("Retry-After");
      const retryAfter = retryHeader ? parseInt(retryHeader, 10) : undefined;
      throw new RateLimitError("Too many requests", retryAfter);
    }
    const t = await resp.text().catch(() => "");
    throw new Error(t || `Failed to fetch thread messages (${resp.status})`);
  }

  const json = await resp.json();
  const messages = Array.isArray(json?.messages) ? json.messages : [];
  return {
    threadId: String(json?.threadId || threadId),
    messages: messages.map((m: any, idx: number) => ({
      id: String(m?.id ?? `${threadId}-${idx}`),
      role: m?.role === "assistant" ? "assistant" : "user",
      text: String(m?.text ?? ""),
      createdAt: String(m?.createdAt ?? new Date().toISOString()),
    })),
  };
}

export async function fetchCurrentUser(): Promise<MeResponse | null> {
  if (DEV_MODE) {
    // In dev mode we don't need an extra call; return null to avoid assumptions
    return null;
  }
  try {
    const resp = await fetch(withBase("/users/me"), {
      method: "GET",
      credentials: "include",
      cache: "no-store",
    });
    if (!resp.ok) {
      console.warn("[api] /users/me failed", { status: resp.status });
      return null;
    }
    return await resp.json();
  } catch (err) {
    console.warn("[api] /users/me network error", err);
    return null;
  }
}
